import { FeatureFlagService } from '@delta-defense/feature-flag-service';
import { ITransaction, Result, Transaction } from '../core/domain/module';
import { BreakingErrors, Messages, QueryParams, Routes } from '../core/enums/module';
import { Customer, Subscription, User } from '../core/models/module';
import { Auth, Subscriptions } from '../core/services/module';
import { Analytics, InternalEventNames, Steps } from '../core/services/analytics/module';
import { getNextRoute } from './getNextRoute';
import { sleep } from './sleep';
import { routeUpgradeAndProductIdPurchases } from './routeUpgradeAndProductIdPurchases';
import { getQueryParameter } from './getQueryParameter';

export const handleLoginResult = (
	route: Routes,
	router: {
		push: (url: string) => void;
	},
	auth = Auth.Instance(),
	subscriptions = Subscriptions.Instance(),
	analytics = Analytics.Instance(),
	featureFlag = FeatureFlagService.Instance(),
	mainLocation = location,
	transaction?: ITransaction
) => {
	const locationSearch = mainLocation.search;
	const transactionObject = transaction || Transaction.Instance();
	const productIdSet = !!getQueryParameter(QueryParams.ProductId, locationSearch);

	async function processLoggedInUser(): Promise<boolean> {
		const success = await getCustomerInfo();
		await analytics.Identify((await auth.getUser()).value);
		await featureFlag.Identify();
		return success;
	}

	async function getCustomerInfo(): Promise<boolean> {
		if (!auth.accessToken) {
			return false;
		}

		transactionObject.Message.Publish(Messages.LoadingShippingAndBilling);

		const customerDataResult = await getResultWithRetry<{
			customer: Customer | null;
			subscriptions: Subscription[];
		}>(
			() => subscriptions.getCustomer(),
			InternalEventNames.ReAttemptingToGetCustomerData,
			BreakingErrors.FailedToLoadCustomerData
		);

		const userDataResult = await getResultWithRetry<User>(
			() => auth.getUser(),
			InternalEventNames.ReAttemptingToGetUserData,
			BreakingErrors.FailedToLoadUserData
		);

		if (!customerDataResult.wasSuccessful || !userDataResult.wasSuccessful) {
			return false;
		}

		transactionObject.Data.customerObject = customerDataResult.value?.customer;
		transactionObject.Data.customerSubscriptions = customerDataResult.value?.subscriptions;

		const activeSubs = customerDataResult.value?.subscriptions.filter((s) => s.attributes.status === 'active');
		if (activeSubs?.length || productIdSet) {
			transactionObject.Message.Publish(Messages.Empty);
			await routeUpgradeAndProductIdPurchases(transactionObject, router, mainLocation);
			return false;
		}

		transactionObject.Message.Publish(Messages.Empty);
		return true;
	}

	async function getResultWithRetry<T>(
		query: () => Promise<Result<T>>,
		internalEventName: InternalEventNames,
		breakingError: BreakingErrors
	) {
		let queryResult = await query();

		if (!queryResult.wasSuccessful) {
			analytics.TrackInternal({
				event_name: internalEventName,
				step: Steps.One
			});

			await sleep(1000);

			queryResult = await query();
			if (!queryResult.wasSuccessful) {
				transactionObject.Error.Publish(breakingError);
			}
		}

		return queryResult;
	}

	return async (result?: Result) => {
		if (result?.wasSuccessful) {
			const success = await processLoggedInUser();
			result.wasSuccessful = success;
			if (success) {
				router.push(`${getNextRoute(route)}${location.search}`);
			}
		}
	};
};
